import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Activity as ActivityIcon, Download, AlertCircle } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, Spinner, EmptyState } from "@/components/ui/page";
import { StageBadge } from "@/components/StageBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/lib/format";

export default function ActivityPage() {
  const [sp] = useSearchParams();
  const { selected, isAll, currentClient } = useClient();
  const [type, setType] = useState(sp.get("type") || "ALL");
  const [stage, setStage] = useState("ALL");
  const [automated, setAutomated] = useState("ALL");
  const start = sp.get("start");
  const end = sp.get("end");

  const single = !isAll && selected;

  const { data, isLoading } = useQuery({
    queryKey: ["activities", selected, type, stage, automated, start, end],
    queryFn: async () => (await api.get(`/clients/${selected}/activities`, {
      params: {
        type: type === "ALL" ? undefined : type,
        stage: stage === "ALL" ? undefined : stage,
        automated: automated === "ALL" ? undefined : automated,
        start: start || undefined, end: end || undefined, page_size: 200,
      },
    })).data,
    enabled: !!single,
  });

  const exportUrl = useMemo(() => {
    const t = localStorage.getItem("vsm_token");
    const params = new URLSearchParams({ dataset: "activities" });
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    return `${API_BASE}/clients/${selected}/export?${params.toString()}&token=${t}`;
  }, [selected, start, end]);

  const download = async () => {
    const res = await api.get(`/clients/${selected}/export`, { params: { dataset: "activities", start, end }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "activities.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (isAll) return <div><PageHeader title="Activity" /><EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client to view its activity log." /></div>;

  const items = data?.items || [];

  return (
    <div>
      <PageHeader title="Activity" subtitle={currentClient?.business_name}>
        <Button variant="outline" onClick={download} data-testid="export-activity-btn"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        <Filter label="Type" value={type} onChange={setType} options={["ALL", "CALL", "TEXT", "EMAIL", "MAILER"]} testid="activity-type-filter" />
        <Filter label="Stage" value={stage} onChange={setStage} options={["ALL", "LEAD", "PROSPECT", ["RE_ENGAGED", "RE-ENGAGED"], "DEAL", "WON", "LOST"]} testid="activity-stage-filter" />
        <Filter label="Source" value={automated} onChange={setAutomated} options={[["ALL", "All"], ["false", "Human"], ["true", "Automated"]]} testid="activity-automated-filter" />
      </div>

      {isLoading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={ActivityIcon} title="No activities" subtitle="No activities match these filters for the selected period." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Date</TableHead><TableHead>Contact</TableHead><TableHead>Stage</TableHead>
                  <TableHead>Activity</TableHead><TableHead>User</TableHead><TableHead>Outcome</TableHead><TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} data-testid={`activity-row-${a.id}`}>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{fmtDateTime(a.date)}</TableCell>
                    <TableCell className="font-medium text-slate-800">{a.contact_name}</TableCell>
                    <TableCell><StageBadge stage={a.related_stage} /></TableCell>
                    <TableCell><span className="font-semibold text-slate-700">{a.type}</span>{a.automated && <span className="ml-1.5 text-[10px] bg-slate-100 text-slate-500 px-1 rounded">AUTO</span>}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.user_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.status || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-[240px] truncate">{a.notes || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 text-sm text-slate-500">{items.length} activities</div>
        </div>
      )}
    </div>
  );
}

function Filter({ label, value, onChange, options, testid }) {
  const opts = options.map((o) => Array.isArray(o) ? o : [o, o === "ALL" ? `All ${label}s` : o]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[160px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
